import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	pointerWithin,
	rectIntersection,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import type { Active, CollisionDetection, DragEndEvent, DragMoveEvent, DragStartEvent } from "@dnd-kit/core";
import { Badge, Button, Dropdown, Empty, Modal, Select, Space, Tag, Tooltip, Typography } from "antd";
import type { MenuProps } from "antd";
import {
	CalendarOutlined,
	CheckOutlined,
	CopyOutlined,
	DeleteOutlined,
	EditOutlined,
	FilePdfOutlined,
	LeftOutlined,
	MoreOutlined,
	RightOutlined,
	ScheduleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
	getDayCount,
	getRtpRights,
	getUserName,
	isActivePhase,
	isPhaseLocked,
	isPlanReadOnly,
	phaseDateMeta,
	phaseDateRanges,
} from "@utils/rtp_utils";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { rtpApi, runRtpWrite, useCurrentUserUuid, useRtpMetricOptions } from "./rtpHooks";
import type { RtpBlockLibraryState } from "./rtpHooks";
import { BlockContentView } from "./BlockContentView";
import { BlockDetailModal } from "./BlockDetailModal";
import { COACH_COLORS, CreatorChip, coachColors } from "./CreatorChip";
import { EditableValue, ExerciseTable } from "./ExerciseTable";
import { BlockLibraryPopover } from "./BlockLibraryPopover";
import { AddBlockModal, ShiftBlocksModal } from "./BlockModals";
import { QuickAddBlock } from "./QuickAddBlock";
import type { QuickAddHandle } from "./QuickAddBlock";
import { ScheduleExportModal } from "./ScheduleExportModal";
import type { PlanTabProps } from "./OverviewTab";
import type { RtpBlock, RtpBlockContent, RtpBlockTemplate, RtpGymExercise, RtpPhase, RtpPlanDetail } from "./types";

// Phase picker entry: the number avatar in its state color (as on the Overview cards), the name,
// a status pill, and the dates. `detailed` = the dropdown list; the closed select is one line.
const PhaseOptionLabel = ({
	phase,
	number,
	active,
	detailed,
}: {
	phase: RtpPhase;
	number: number;
	active: boolean;
	detailed: boolean;
}) => {
	const stateClass = phase.completedAt ? " rtp-phase-num--done" : active ? " rtp-phase-num--active" : "";
	const blocks = `${phase.blocks.length} block${phase.blocks.length === 1 ? "" : "s"}`;
	return (
		<div className={`rtp-phase-opt${detailed ? " rtp-phase-opt--detailed" : ""}`}>
			<span className={`rtp-phase-num rtp-phase-num--sm${stateClass}`}>
				{phase.completedAt ? <CheckOutlined /> : number}
			</span>
			<div className="rtp-phase-opt-text">
				<div className="rtp-phase-opt-name">
					<span className="rtp-phase-opt-title">{phase.name}</span>
					{detailed && phase.completedAt ? (
						<span className="rtp-status-pill rtp-status-pill--completed">Completed</span>
					) : null}
					{detailed && active ? (
						<span className="rtp-status-pill rtp-status-pill--active">
							<Badge status="processing" /> Active
						</span>
					) : null}
					{detailed ? null : <span className="rtp-phase-opt-meta">{phaseDateMeta(phase, false)}</span>}
				</div>
				{detailed ? <div className="rtp-phase-opt-meta">{`${phaseDateMeta(phase, false)} · ${blocks}`}</div> : null}
			</div>
		</div>
	);
};


// Free text clamps to a few lines; the full text shows in a tooltip on hover. `lead` (the coach
// chip) goes inline at the start of the text, or alone in its own row when the text is empty.
const ClampedText = ({ text, rows, lead }: { text: string; rows: number; lead: ReactNode }) =>
	text.trim() ? (
		<Typography.Paragraph className="rtp-block-text" ellipsis={{ rows, tooltip: text }}>
			{lead}
			{text}
		</Typography.Paragraph>
	) : (
		<div className="rtp-block-lead">{lead}</div>
	);

// Copy of `obj` with `key` set to the trimmed value, or removed when empty — the same
// "omit empty fields" rule the block form uses when it builds content.
function withValue<T extends object>(obj: T, key: keyof T, value: string): T {
	const next = { ...obj };
	const trimmed = value.trim();
	if (trimmed) (next as Record<keyof T, unknown>)[key] = trimmed;
	else delete next[key];
	return next;
}

// Card body: the description (2 rows) with the coach chip (`lead`) at its start, then the exercise
// table, then the target lines, each only when present. The full exercise and target lists stay
// visible; only the description clamps. Short values edit in place when `onSaveContent` is given;
// long text edits in the Block dialog. The exercise table is shared with the Block Library cards.
const BlockCardBody = ({
	block,
	lead,
	metricLabel,
	onSaveContent,
}: {
	block: RtpBlock;
	lead: ReactNode;
	metricLabel: (key: string) => string;
	onSaveContent?: (next: RtpBlockContent) => void;
}) => {
	const { content } = block;
	const { description, exercises, targets } = content;
	const saveExercise = (i: number, field: keyof RtpGymExercise) =>
		onSaveContent
			? (next: string) =>
					onSaveContent({
						...content,
						exercises: exercises.map((ex, j) => (j === i ? withValue(ex, field, next) : ex)),
					})
			: undefined;
	const saveTarget = (i: number) =>
		onSaveContent
			? (next: string) =>
					onSaveContent({
						...content,
						targets: targets.map((t, j) => (j === i ? withValue(t, "target", next) : t)),
					})
			: undefined;
	return (
		<>
			<ClampedText text={description} rows={2} lead={lead} />
			{exercises.length > 0 ? <ExerciseTable exercises={exercises} onSaveExercise={saveExercise} compact /> : null}
			{targets.map((t, i) => (
				<div key={`${t.metricKey}-${i}`} className="rtp-block-line">
					<span className="rtp-block-line-name">{metricLabel(t.metricKey)}</span>
					<span className="rtp-block-line-value">
						<EditableValue value={t.target} onSave={saveTarget(i)} label="Target" />
					</span>
				</div>
			))}
		</>
	);
};

interface BlockCardProps extends HTMLAttributes<HTMLDivElement> {
	block: RtpBlock;
	creator?: string;
	color: string;
	metricLabel: (key: string) => string;
	// The ⋯ menu and inline value edits — editors only, and never on the drag overlay copy.
	menu?: ReactNode;
	onSaveContent?: (next: RtpBlockContent) => void;
	cardRef?: (el: HTMLElement | null) => void;
}

const BlockCard = ({
	block,
	creator,
	color,
	metricLabel,
	menu,
	onSaveContent,
	cardRef,
	className = "",
	...rest
}: BlockCardProps) => (
	<div ref={cardRef} className={`rtp-block-card ${className}`} style={{ borderLeftColor: color }} {...rest}>
		{menu}
		<BlockCardBody
			block={block}
			lead={<CreatorChip creator={creator} color={color} />}
			metricLabel={metricLabel}
			onSaveContent={onSaveContent}
		/>
	</div>
);

// Each card is draggable, and also a drop target: its top half means "insert before this
// card", its bottom half "insert after it". The 6px activation distance (set on the sensor)
// keeps a plain click opening the modal.
const DraggableBlockCard = ({ dragDisabled, ...props }: BlockCardProps & { dragDisabled: boolean }) => {
	const drag = useDraggable({ id: props.block.id, disabled: dragDisabled });
	const drop = useDroppable({ id: `card-${props.block.id}`, disabled: dragDisabled });
	const setRefs = (el: HTMLElement | null) => {
		drag.setNodeRef(el);
		drop.setNodeRef(el);
	};
	const stateClass = `${dragDisabled ? "" : "rtp-block-card--draggable"}${drag.isDragging ? " rtp-block-card--dragging" : ""}`;
	return <BlockCard {...props} {...drag.attributes} {...drag.listeners} cardRef={setRefs} className={stateClass} />;
};

// The day body is the fallback target: over empty space it means "the end of this day".
const DayDropZone = ({
	dayIndex,
	enabled,
	highlighted,
	children,
}: {
	dayIndex: number;
	enabled: boolean;
	highlighted: boolean;
	children: ReactNode;
}) => {
	const { setNodeRef } = useDroppable({ id: `day-${dayIndex}`, disabled: !enabled });
	return (
		<div ref={setNodeRef} className={`rtp-daycol-body${highlighted ? " rtp-daycol-body--over" : ""}`}>
			{children}
		</div>
	);
};

// Drag preview of a library entry: a card like the ones on the grid, in the color and with the
// initials of the user who adds it.
const LibraryDragCard = ({
	entry,
	creator,
	color,
	metricLabel,
}: {
	entry: RtpBlockTemplate;
	creator?: string;
	color: string;
	metricLabel: (key: string) => string;
}) => (
	<div className="rtp-block-card rtp-block-card--overlay rtp-lib-drag-card" style={{ borderLeftColor: color }}>
		<BlockContentView
			content={entry.content}
			metricLabel={metricLabel}
			lead={<CreatorChip creator={creator} color={color} />}
			compact
		/>
	</div>
);

// The library entry that a drag carries, or null for a block move.
const getDraggedEntry = (active: Active): RtpBlockTemplate | null => active.data.current?.entry ?? null;

// A card under the pointer wins over the day body that contains it. A library row has no card
// shape to test, so it lands only on a target under the pointer.
const preferCardTargets: CollisionDetection = (args) => {
	const hits = pointerWithin(args);
	const cardHit = hits.find((hit) => String(hit.id).startsWith("card-"));
	if (cardHit) return [cardHit];
	if (hits.length > 0 || getDraggedEntry(args.active)) return hits;
	return rectIntersection(args);
};

// Where a drop lands: a day, and the card to insert before (null = the end of the day).
interface DropTarget {
	day: number;
	beforeId: number | null;
}

export interface WeekCell {
	dayIndex: number;
	date: Dayjs | null;
	inPhase: boolean;
}

// Calendar Mon–Sun weeks covering the phase; leading/trailing days outside the phase come back
// with inPhase: false (rendered greyed out). Dateless (draft) phases fall back to plain
// 7-day chunks of phase days.
export function buildWeeks(phase: RtpPhase, dayCount: number): WeekCell[][] {
	const weeks: WeekCell[][] = [];
	if (phase.startDate) {
		const start = dayjs(phase.startDate).startOf("day");
		const firstMonday = start.subtract((start.day() + 6) % 7, "day");
		const totalDays = start.add(dayCount - 1, "day").diff(firstMonday, "day") + 1;
		for (let w = 0; w < Math.ceil(totalDays / 7); w++) {
			weeks.push(
				Array.from({ length: 7 }, (_, c) => {
					const date = firstMonday.add(w * 7 + c, "day");
					const dayIndex = date.diff(start, "day") + 1;
					return { dayIndex, date, inPhase: dayIndex >= 1 && dayIndex <= dayCount };
				})
			);
		}
	} else {
		for (let w = 0; w < Math.ceil(dayCount / 7); w++) {
			weeks.push(
				Array.from({ length: 7 }, (_, c) => {
					const dayIndex = w * 7 + c + 1;
					return { dayIndex, date: null, inPhase: dayIndex <= dayCount };
				})
			);
		}
	}
	return weeks;
}

export const DayPlansTab = ({
	plan,
	refetch,
	library,
	onOpenLibraryEntry,
}: PlanTabProps & {
	library: RtpBlockLibraryState;
	// Opens the Block Library tab on one entry. The row detail of the library popover calls it.
	onOpenLibraryEntry?: (entryId: string) => void;
}) => {
	const currentUserUuid = useCurrentUserUuid();
	const rights = getRtpRights(plan, currentUserUuid);
	const { byKey: metricByKey } = useRtpMetricOptions();
	const metricLabel = (key: string) => metricByKey[key]?.label ?? key;

	const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(
		() => plan.activePhaseId ?? plan.phases[0]?.id ?? null
	);
	const [selectedBlockId, setSelectedBlockId] = useState<number | null>(null);
	const [addModalOpen, setAddModalOpen] = useState(false);
	// The day whose "+" has its popover open. Only that popover holds the ref.
	const [quickAddDay, setQuickAddDay] = useState<number | null>(null);
	const quickAddRef = useRef<QuickAddHandle>(null);
	const [shiftModalOpen, setShiftModalOpen] = useState(false);
	const [exportOpen, setExportOpen] = useState(false);
	const [libOpen, setLibOpen] = useState(false);

	// Guard against the selected phase disappearing (deleted) after a refetch — fall back to the
	// active phase, else the first one. Leaves a valid selection alone.
	useEffect(() => {
		if (selectedPhaseId != null && plan.phases.some((p) => p.id === selectedPhaseId)) return;
		setSelectedPhaseId(plan.activePhaseId ?? plan.phases[0]?.id ?? null);
	}, [plan, selectedPhaseId]);

	const selectedPhase = plan.phases.find((p) => p.id === selectedPhaseId) ?? null;

	// Derived, not stored: re-resolves the drawer's block from fresh plan data on every refetch,
	// and naturally reads as "closed" (block: null) if the block was deleted out from under it.
	const selectedBlock = useMemo(
		() => selectedPhase?.blocks.find((b) => b.id === selectedBlockId) ?? null,
		[selectedPhase, selectedBlockId]
	);

	const [weekIndex, setWeekIndex] = useState(0);
	// A new phase or week closes the open popover.
	useEffect(() => {
		setQuickAddDay(null);
	}, [selectedPhaseId, weekIndex]);
	// A new phase closes the library popover. A new week keeps it open.
	useEffect(() => {
		setLibOpen(false);
	}, [selectedPhaseId]);
	// Coach filter: creator uuids to show; empty = every coach.
	const [coachFilter, setCoachFilter] = useState<string[]>([]);

	// Land on today's week when opening the active phase; week 1 otherwise. Deliberately keyed on
	// the phase id only — a plan refetch after a write must not yank the user back to today.
	useEffect(() => {
		const phase = plan.phases.find((p) => p.id === selectedPhaseId);
		if (!phase) {
			setWeekIndex(0);
			return;
		}
		const phaseWeeks = buildWeeks(phase, getDayCount(phase));
		const today =
			phase.startDate && isActivePhase(plan, phase)
				? dayjs().startOf("day").diff(dayjs(phase.startDate), "day") + 1
				: null;
		const idx =
			today != null ? phaseWeeks.findIndex((wk) => wk.some((c) => c.inPhase && c.dayIndex === today)) : -1;
		setWeekIndex(idx >= 0 ? idx : 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPhaseId]);

	// Only 3–4 day columns fit the width, so each week change scrolls today's column (or the
	// first in-phase day) into view instead of leaving greyed padding days in front.
	const scrollerRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;
		const target =
			scroller.querySelector<HTMLElement>(".rtp-daycol--today") ??
			scroller.querySelector<HTMLElement>(".rtp-daycol:not(.rtp-daycol--out)");
		scroller.scrollTo({ left: target ? target.offsetLeft : 0, behavior: "smooth" });
	}, [selectedPhaseId, weekIndex]);

	// Edge fades show that more days exist to the left or right (the scrollbar is hidden).
	const [edgeFade, setEdgeFade] = useState({ left: false, right: false });
	const syncEdgeFade = () => {
		const el = scrollerRef.current;
		if (!el) return;
		const left = el.scrollLeft > 1;
		const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
		setEdgeFade((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
	};
	// No deps on purpose: column widths change with the week, the filter, and new blocks.
	useEffect(syncEdgeFade);
	useEffect(() => {
		window.addEventListener("resize", syncEdgeFade);
		return () => window.removeEventListener("resize", syncEdgeFade);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
	const [draggingId, setDraggingId] = useState<number | null>(null);
	// The library entry in a drag, and whether its drop is in a write. Both keep the popover faded.
	const [draggingEntry, setDraggingEntry] = useState<RtpBlockTemplate | null>(null);
	const [libAdding, setLibAdding] = useState(false);
	const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
	// Optimistic moves (block id → new day + a fractional sort order), shown until the refetched
	// plan arrives, so a dropped card does not jump back for the length of the round trip.
	const [pendingMoves, setPendingMoves] = useState<Record<number, { dayIndex: number; order: number }>>({});
	// Inline value edits (block id → edited content), same lifetime. The backend replaces the whole
	// `content` on each PATCH, so a second quick save must build on the first one's local result,
	// not on the not-yet-refetched plan — otherwise the first edit would be lost.
	const [pendingContent, setPendingContent] = useState<Record<number, RtpBlockContent>>({});
	useEffect(() => {
		setPendingMoves({});
		setPendingContent({});
	}, [plan]);

	if (plan.phases.length === 0) {
		return <Empty description="No phases yet — add one from the Overview tab." />;
	}

	const editable =
		selectedPhase != null && rights.canCreateDayPlan && !isPlanReadOnly(plan) && !isPhaseLocked(selectedPhase);

	const handlePhaseChange = (value: number) => {
		setSelectedPhaseId(value);
		setSelectedBlockId(null);
	};

	// A popover with content stays open and shakes. An empty popover moves to the new day.
	const openQuickAdd = (day: number) => {
		const open = quickAddRef.current;
		if (open?.hasContent()) open.shake();
		else setQuickAddDay(day);
	};

	// Card ⋯ menu. Duplicate copies the block onto the same day, directly under the original
	// (ADD_BLOCK with `order` shifts the later blocks of that day down by one). Edit opens the
	// Block dialog, which is in edit mode for editors.
	// "Copy to next <weekday>": the same day one week later. That date can fall in a later phase,
	// so the copy goes to whichever phase holds it; a completed phase or a date after the plan
	// ends disables the item. Dateless (draft) phases copy to day + 7 within the same phase.
	const nextWeekTarget = (block: RtpBlock): { phase: RtpPhase; dayIndex: number; date: Dayjs | null } | null => {
		if (!selectedPhase) return null;
		if (!selectedPhase.startDate) {
			const dayIndex = block.dayIndex + 7;
			return dayIndex <= getDayCount(selectedPhase) ? { phase: selectedPhase, dayIndex, date: null } : null;
		}
		const date = dayjs(selectedPhase.startDate).startOf("day").add(block.dayIndex + 6, "day");
		const range = phaseDateRanges(plan).find((r) => !date.isBefore(r.start) && !date.isAfter(r.end));
		if (!range || isPhaseLocked(range.phase)) return null;
		return { phase: range.phase, dayIndex: date.diff(range.start, "day") + 1, date };
	};

	const copyNextWeekLabel = (block: RtpBlock) => {
		const target = nextWeekTarget(block);
		if (!selectedPhase?.startDate) return `Copy to day ${block.dayIndex + 7}`;
		const weekday = dayjs(selectedPhase.startDate).add(block.dayIndex - 1, "day").format("dddd");
		return target?.date ? `Copy to next ${weekday} · ${target.date.format("MMM D")}` : `Copy to next ${weekday}`;
	};

	const cardMenuItems = (block: RtpBlock): MenuProps["items"] => {
		const target = nextWeekTarget(block);
		return [
			{ key: "edit", icon: <EditOutlined />, label: <span data-attr="settings.rtp-block.card-edit">Edit</span> },
			{ key: "duplicate", icon: <CopyOutlined />, label: <span data-attr="settings.rtp-block.card-duplicate">Duplicate</span> },
			{
				key: "copy-next-week",
				icon: <CalendarOutlined />,
				disabled: !target,
				label: (
					<Tooltip title={target ? undefined : "Next week falls in a completed phase or after the plan ends."}>
						<span data-attr="settings.rtp-block.card-copy-next-week">{copyNextWeekLabel(block)}</span>
					</Tooltip>
				),
			},
			{ type: "divider" },
			{ key: "delete", icon: <DeleteOutlined />, danger: true, label: <span data-attr="settings.rtp-block.card-delete">Delete</span> },
		];
	};

	// ADD_BLOCK gets a 400 for the id of a deleted entry, so a copy keeps the link only to a live entry.
	const liveTemplateId = (block: RtpBlock) =>
		library.templates.find((entry) => entry.id === block.templateId)?.id;

	const handleCardMenu = (block: RtpBlock, key: string) => {
		if (key === "edit") {
			setSelectedBlockId(block.id);
			return;
		}
		if (key === "copy-next-week") {
			const target = nextWeekTarget(block);
			if (!target) return;
			const where = target.date ? target.date.format("ddd, MMM D") : `day ${target.dayIndex}`;
			const phaseNote = target.phase.id === selectedPhase?.id ? "" : ` (${target.phase.name})`;
			const templateId = liveTemplateId(block);
			runRtpWrite(
				() =>
					rtpApi.rtpPhaseAction(target.phase.id, {
						action: "ADD_BLOCK",
						dayIndexes: [target.dayIndex],
						content: block.content,
						templateId,
					}),
				() => {
					refetch();
					if (templateId) library.reload();
					message.success(`Block copied to ${where}${phaseNote}.`);
				}
			);
			return;
		}
		if (key === "duplicate") {
			const templateId = liveTemplateId(block);
			runRtpWrite(
				() =>
					rtpApi.rtpPhaseAction(selectedPhaseId, {
						action: "ADD_BLOCK",
						dayIndexes: [block.dayIndex],
						order: block.order + 1,
						content: block.content,
						templateId,
					}),
				() => {
					refetch();
					if (templateId) library.reload();
					message.success("Block duplicated.");
				}
			);
			return;
		}
		Modal.confirm({
			title: "Delete this block?",
			content: "This can't be undone.",
			okText: "Delete",
			okType: "danger",
			okButtonProps: { "data-attr": "settings.rtp-block.card-delete-submit" } as any,
			onOk: () =>
				runRtpWrite(
					() => rtpApi.deleteRtpBlock(block.id),
					() => {
						refetch();
						// The block was a use of its entry, so the usage counts change.
						if (block.templateId) library.reload();
					}
				),
		});
	};

	const saveContent = (block: RtpBlock, content: RtpBlockContent) => {
		setPendingContent((prev) => ({ ...prev, [block.id]: content }));
		runRtpWrite(() => rtpApi.patchRtpBlock(block.id, { content }), refetch).then((ok) => {
			if (ok) return;
			setPendingContent((prev) => {
				const next = { ...prev };
				delete next[block.id];
				return next;
			});
		});
	};

	// Coach = the staff member who created the block. Options come from the whole plan, so the
	// list stays put when you switch phases.
	const coachOptions = [...new Set(plan.phases.flatMap((p) => p.blocks.map((b) => b.createdBy)))]
		.map((uuid) => ({ uuid, name: getUserName(plan, uuid) }))
		.sort((a, b) => a.name.localeCompare(b.name));
	const toggleCoach = (uuid: string) =>
		setCoachFilter((prev) => (prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]));

	const colorByCoach = coachColors(plan);
	// A user with no block yet gets the next color of the list when the first block lands.
	const libraryDragColor =
		colorByCoach[currentUserUuid] ?? COACH_COLORS[Object.keys(colorByCoach).length % COACH_COLORS.length];

	const sortedBlocks = selectedPhase
		? selectedPhase.blocks
				.filter((b) => coachFilter.length === 0 || coachFilter.includes(b.createdBy))
				.map((b) => ({
					...b,
					...pendingMoves[b.id],
					...(pendingContent[b.id] ? { content: pendingContent[b.id] } : {}),
				}))
				.sort((a, b) => a.dayIndex - b.dayIndex || a.order - b.order)
		: [];
	const draggingBlock = sortedBlocks.find((b) => b.id === draggingId) ?? null;
	const dayList = (day: number) => sortedBlocks.filter((b) => b.dayIndex === day);

	const resolveDropTarget = ({ active, over, activatorEvent, delta }: DragMoveEvent): DropTarget | null => {
		if (!over) return null;
		const overId = String(over.id);
		let target: DropTarget;
		if (overId.startsWith("day-")) {
			target = { day: Number(overId.slice(4)), beforeId: null };
		} else {
			const overBlock = sortedBlocks.find((b) => b.id === Number(overId.slice(5)));
			if (!overBlock) return null;
			const pointerY = (activatorEvent as PointerEvent).clientY + delta.y;
			const siblings = dayList(overBlock.dayIndex);
			const inTopHalf = pointerY < over.rect.top + over.rect.height / 2;
			const before = inTopHalf ? overBlock : siblings[siblings.indexOf(overBlock) + 1];
			target = { day: overBlock.dayIndex, beforeId: before?.id ?? null };
		}
		// A drop that leaves the card where it is shows no line and sends nothing. A library entry
		// is no card on the grid (`dragged` is empty), so each of its targets stands.
		const dragged = sortedBlocks.find((b) => b.id === active.id);
		if (!dragged || target.day !== dragged.dayIndex) return target;
		const siblings = dayList(dragged.dayIndex);
		const after = siblings[siblings.indexOf(dragged) + 1];
		const unchanged = target.beforeId === dragged.id || target.beforeId === (after?.id ?? null);
		return unchanged ? null : target;
	};

	const handleDragStart = ({ active }: DragStartEvent) => {
		const entry = getDraggedEntry(active);
		if (entry) setDraggingEntry(entry);
		else setDraggingId(Number(active.id));
	};

	const handleDragCancel = () => {
		setDraggingId(null);
		setDraggingEntry(null);
		setDropTarget(null);
	};

	// One ADD_BLOCK that sends the entry content and its id. Without `order`, the backend puts the
	// block at the bottom of the day. The popover stays faded until the write ends. It closes on
	// success, and a failed write leaves it open.
	const addLibraryEntry = (entry: RtpBlockTemplate, target: DropTarget) => {
		if (!selectedPhase) return;
		const phaseId = selectedPhase.id;
		const before = selectedPhase.blocks.find((b) => b.id === target.beforeId);
		const where = selectedPhase.startDate
			? dayjs(selectedPhase.startDate).add(target.day - 1, "day").format("ddd D")
			: `day ${target.day}`;
		setLibAdding(true);
		runRtpWrite(
			() =>
				rtpApi.rtpPhaseAction(phaseId, {
					action: "ADD_BLOCK",
					dayIndexes: [target.day],
					order: before?.order,
					content: entry.content,
					templateId: entry.id,
				}),
			() => {
				refetch();
				library.reload();
				setLibOpen(false);
				message.success(`Added “${entry.title}” to ${where}.`);
			}
		).then(() => setLibAdding(false));
	};

	// One PATCH, one block_updated event. The backend reads `order` as "insert before the
	// block that has this order now; past the end = the bottom of the day".
	const handleDragEnd = (event: DragEndEvent) => {
		const target = resolveDropTarget(event);
		const entry = getDraggedEntry(event.active);
		setDraggingId(null);
		setDraggingEntry(null);
		setDropTarget(null);
		if (entry) {
			if (target) addLibraryEntry(entry, target);
			return;
		}
		const block = selectedPhase?.blocks.find((b) => b.id === event.active.id);
		if (!selectedPhase || !block || !target) return;
		const before = target.beforeId != null ? sortedBlocks.find((b) => b.id === target.beforeId) : undefined;
		const dayOrders = selectedPhase.blocks.filter((b) => b.dayIndex === target.day && b.id !== block.id).map((b) => b.order);
		const lastOrder = Math.max(0, ...dayOrders);
		const order = before ? before.order : lastOrder + 1;
		setPendingMoves((prev) => ({
			...prev,
			[block.id]: { dayIndex: target.day, order: before ? before.order - 0.5 : lastOrder + 0.5 },
		}));
		runRtpWrite(() => rtpApi.patchRtpBlock(block.id, { dayIndex: target.day, order }), refetch).then((ok) => {
			if (ok) return;
			setPendingMoves((prev) => {
				const next = { ...prev };
				delete next[block.id];
				return next;
			});
		});
	};
	const dayCount = selectedPhase ? getDayCount(selectedPhase) : 0;
	const weeks = selectedPhase ? buildWeeks(selectedPhase, dayCount) : [];
	const clampedWeek = Math.min(weekIndex, Math.max(weeks.length - 1, 0));
	const week = weeks[clampedWeek] ?? [];

	// Highlight today's column while this phase is the live one.
	const todayIndex =
		selectedPhase?.startDate && isActivePhase(plan, selectedPhase)
			? dayjs().startOf("day").diff(dayjs(selectedPhase.startDate), "day") + 1
			: null;
	const isToday = (cell: WeekCell) => cell.inPhase && todayIndex === cell.dayIndex;

	const datedWeek = week.length > 0 && week[0].date != null;
	const weekRangeLabel = datedWeek ? `${week[0].date!.format("MMM D")} – ${week[6].date!.format("MMM D")}` : "";

	return (
		<div>
			<DndContext
				sensors={dragSensors}
				collisionDetection={preferCardTargets}
				onDragStart={handleDragStart}
				onDragMove={(event) => setDropTarget(resolveDropTarget(event))}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 16,
						flexWrap: "wrap",
						marginBottom: 12,
					}}
				>
					<Space size={8} wrap>
						<Select
							className="rtp-phase-select"
							// The popup portals outside .rtp-scope — rtp-wz carries the design tokens there.
							classNames={{ popup: { root: "rtp-wz rtp-phase-select-pop" } }}
							value={selectedPhaseId ?? undefined}
							onChange={handlePhaseChange}
							options={plan.phases.map((phase, index) => ({ value: phase.id, label: phase.name, index }))}
							optionRender={(option) => (
								<PhaseOptionLabel
									phase={plan.phases[option.data.index]}
									number={option.data.index + 1}
									active={isActivePhase(plan, plan.phases[option.data.index])}
									detailed
								/>
							)}
							labelRender={({ value }) => {
								const index = plan.phases.findIndex((p) => p.id === value);
								if (index < 0) return null;
								return (
									<PhaseOptionLabel
										phase={plan.phases[index]}
										number={index + 1}
										active={isActivePhase(plan, plan.phases[index])}
										detailed={false}
									/>
								);
							}}
						/>
						{weeks.length > 1 ? (
							<div className="rtp-week-pager">
								<Button
									type="text"
									size="small"
									icon={<LeftOutlined />}
									aria-label="Previous week"
									data-attr="settings.rtp-block.week-prev"
									disabled={clampedWeek === 0}
									onClick={() => setWeekIndex(clampedWeek - 1)}
								/>
								<span className="rtp-week-pager-label">
									<span className="rtp-week-pager-week">
										Week {clampedWeek + 1} of {weeks.length}
									</span>
									{weekRangeLabel ? <span className="rtp-week-pager-range">{weekRangeLabel}</span> : null}
								</span>
								<Button
									type="text"
									size="small"
									icon={<RightOutlined />}
									aria-label="Next week"
									data-attr="settings.rtp-block.week-next"
									disabled={clampedWeek >= weeks.length - 1}
									onClick={() => setWeekIndex(clampedWeek + 1)}
								/>
							</div>
						) : null}
					</Space>
					<Space>
						<BlockLibraryPopover
							library={library}
							open={libOpen}
							onOpenChange={setLibOpen}
							faded={draggingEntry != null || libAdding}
							canDrag={editable}
							metricLabel={metricLabel}
							onOpenEntry={onOpenLibraryEntry}
						/>
						<Tooltip title={plan.startDate ? undefined : "Start the plan to export its schedule."}>
							<Button
								color="primary"
								variant="solid"
								icon={<FilePdfOutlined />}
								data-attr="settings.rtp-schedule-export.open"
								disabled={!plan.startDate}
								onClick={() => setExportOpen(true)}
							>
								Export
							</Button>
						</Tooltip>
						{editable && selectedPhase ? (
							<Button
								color="primary"
								variant="solid"
								icon={<ScheduleOutlined />}
								data-attr="settings.rtp-block.add"
								onClick={() => setAddModalOpen(true)}
							>
								Add Block
							</Button>
						) : null}
						{/* Shift Blocks is hidden for now — modal and handlers stay wired up.
							<Tooltip title={selectedPhase.blocks.length === 0 ? "No blocks in this phase yet" : undefined}>
								<span>
									<Button
										data-attr="settings.rtp-block.shift"
										disabled={selectedPhase.blocks.length === 0}
										onClick={() => setShiftModalOpen(true)}
									>
										Shift Blocks
									</Button>
								</span>
							</Tooltip>
							*/}
					</Space>
				</div>

				{coachOptions.length > 0 ? (
					<div className="rtp-pills rtp-coach-filter">
						<Tag.CheckableTag
							checked={coachFilter.length === 0}
							data-attr="settings.rtp-block.filter-coach-all"
							onChange={() => setCoachFilter([])}
						>
							All coaches
						</Tag.CheckableTag>
						{coachOptions.map((coach) => (
							<Tag.CheckableTag
								key={coach.uuid}
								checked={coachFilter.includes(coach.uuid)}
								data-attr="settings.rtp-block.filter-coach"
								onChange={() => toggleCoach(coach.uuid)}
							>
								{/* Legend for the card colors */}
								<span className="rtp-coach-dot" style={{ background: colorByCoach[coach.uuid] }} />
								{coach.name}
							</Tag.CheckableTag>
						))}
					</div>
				) : null}

				{selectedPhase && selectedPhase.blocks.length === 0 ? (
					<div style={{ marginBottom: 12 }}>
						<Typography.Text type="secondary">No blocks in this phase yet.</Typography.Text>
					</div>
				) : selectedPhase && sortedBlocks.length === 0 ? (
					<div style={{ marginBottom: 12 }}>
						<Typography.Text type="secondary">No blocks from the selected coaches in this phase.</Typography.Text>
					</div>
				) : null}
				<div
					className={`rtp-daygrid-frame${edgeFade.left ? " rtp-daygrid-frame--fade-left" : ""}${
						edgeFade.right ? " rtp-daygrid-frame--fade-right" : ""
					}`}
				>
					<div className="rtp-daygrid-wrap" ref={scrollerRef} onScroll={syncEdgeFade}>
						<div
							className="rtp-daygrid"
							style={{
								// Days with no blocks (incl. greyed out-of-phase days) get a narrow column,
								// so the width goes to the days that have content. When the minimums fit,
								// the free width is shared (3 : 1), so a quiet week still fills the frame.
								gridTemplateColumns: week
									.map((cell) =>
										cell.inPhase && sortedBlocks.some((b) => b.dayIndex === cell.dayIndex)
											? "minmax(max(250px, calc(100% / 3)), 3fr)"
											: "minmax(96px, 1fr)"
									)
									.join(" "),
							}}
						>
							{week.map((cell) => {
								const dayBlocks = cell.inPhase ? sortedBlocks.filter((b) => b.dayIndex === cell.dayIndex) : [];
								const colClass = `rtp-daycol${isToday(cell) ? " rtp-daycol--today" : ""}${
									cell.inPhase ? "" : " rtp-daycol--out"
								}${dayBlocks.length === 0 ? " rtp-daycol--empty" : ""}`;
								return (
									<div key={cell.dayIndex} className={colClass}>
										<div className="rtp-daycol-head">
											{cell.date ? (
												<>
													<div className="rtp-daycol-dow">{cell.date.format("ddd D")}</div>
													<div className="rtp-daycol-date">
														{cell.date.format("MMM")}
														{cell.inPhase ? ` · Day ${cell.dayIndex}` : ""}
													</div>
												</>
											) : (
												<div className="rtp-daycol-dow">Day {cell.dayIndex}</div>
											)}
										</div>
										<DayDropZone
											dayIndex={cell.dayIndex}
											enabled={editable && cell.inPhase}
											highlighted={dropTarget?.day === cell.dayIndex}
										>
											{dayBlocks.map((block) => (
												<Fragment key={block.id}>
													{dropTarget?.day === cell.dayIndex && dropTarget.beforeId === block.id ? (
														<div className="rtp-drop-line" />
													) : null}
													<DraggableBlockCard
														block={block}
														creator={plan.users[block.createdBy]?.name}
														color={colorByCoach[block.createdBy]}
														metricLabel={metricLabel}
														dragDisabled={!editable}
														onSaveContent={editable ? (next) => saveContent(block, next) : undefined}
														role="button"
														tabIndex={0}
														onClick={() => setSelectedBlockId(block.id)}
														onKeyDown={(e) => {
															if (e.key === "Enter" || e.key === " ") {
																e.preventDefault();
																setSelectedBlockId(block.id);
															}
														}}
														menu={
															editable ? (
																// Stops pointer and click events here: React events bubble through the
																// dropdown portal to the card (drag start, modal open).
																<span
																	className="rtp-block-menu-wrap"
																	onPointerDown={(e) => e.stopPropagation()}
																	onClick={(e) => e.stopPropagation()}
																	onKeyDown={(e) => e.stopPropagation()}
																>
																	<Dropdown
																		trigger={["click"]}
																		placement="bottomLeft"
																		menu={{
																			items: cardMenuItems(block),
																			onClick: ({ key }) => handleCardMenu(block, key),
																		}}
																	>
																		<Button
																			className="rtp-block-menu"
																			type="text"
																			size="small"
																			icon={<MoreOutlined />}
																			aria-label="Block options"
																			data-attr="settings.rtp-block.card-menu"
																		/>
																	</Dropdown>
																</span>
															) : undefined
														}
													/>
												</Fragment>
											))}
											{dropTarget?.day === cell.dayIndex && dropTarget.beforeId === null ? (
												<div className="rtp-drop-line" />
											) : null}
											{editable && selectedPhase && cell.inPhase ? (
												<QuickAddBlock
													ref={quickAddDay === cell.dayIndex ? quickAddRef : undefined}
													phase={selectedPhase}
													dayIndex={cell.dayIndex}
													player={{ id: plan.playerId, name: plan.playerName }}
													library={library}
													open={quickAddDay === cell.dayIndex}
													onOpen={() => openQuickAdd(cell.dayIndex)}
													onClose={() => setQuickAddDay(null)}
													refetch={refetch}
												/>
											) : null}
										</DayDropZone>
									</div>
								);
							})}
						</div>
					</div>
				</div>
				{/* The overlay takes the size of the dragged row, so a library preview sets its own height. */}
				{/* The z-index sits above the Block library popover (999), which fades while a library row drags. */}
				<DragOverlay dropAnimation={null} zIndex={1000} style={draggingEntry ? { height: "auto" } : undefined}>
					{draggingBlock ? (
						<BlockCard
							block={draggingBlock}
							creator={plan.users[draggingBlock.createdBy]?.name}
							color={colorByCoach[draggingBlock.createdBy]}
							metricLabel={metricLabel}
							className="rtp-block-card--overlay"
						/>
					) : null}
					{draggingEntry ? (
						<LibraryDragCard
							entry={draggingEntry}
							creator={plan.users[currentUserUuid]?.name}
							color={libraryDragColor}
							metricLabel={metricLabel}
						/>
					) : null}
				</DragOverlay>
			</DndContext>

			<BlockDetailModal
				plan={plan}
				block={selectedBlock}
				canEdit={editable}
				library={library}
				onClose={() => setSelectedBlockId(null)}
				refetch={refetch}
			/>
			{selectedPhase ? (
				<>
					<AddBlockModal
						phase={selectedPhase}
						weekCells={week}
						player={{ id: plan.playerId, name: plan.playerName }}
						library={library}
						open={addModalOpen}
						onClose={() => setAddModalOpen(false)}
						refetch={refetch}
					/>
					<ShiftBlocksModal
						phase={selectedPhase}
						open={shiftModalOpen}
						onClose={() => setShiftModalOpen(false)}
						refetch={refetch}
					/>
				</>
			) : null}
			<ScheduleExportModal
				plan={plan}
				open={exportOpen}
				onClose={() => setExportOpen(false)}
				defaultPhaseIds={selectedPhaseId != null ? [selectedPhaseId] : []}
				defaultOwnerUuids={coachFilter}
				ownerOptions={coachOptions}
			/>
		</div>
	);
};
